import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { Toaster } from "@/components/ui/sonner";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SetPassword = lazy(() => import("@/pages/SetPassword"));
const FirmLayout = lazy(() => import("@/layouts/FirmLayout"));
const TenantLayout = lazy(() => import("@/layouts/TenantLayout"));
const PortalLayout = lazy(() => import("@/layouts/PortalLayout"));
const FirmDashboard = lazy(() => import("@/pages/firm/FirmDashboard"));
const TenantOverview = lazy(() => import("@/pages/firm/TenantOverview"));
const ImportWizard = lazy(() => import("@/pages/firm/ImportWizard"));
const AccountMapping = lazy(() => import("@/pages/firm/AccountMapping"));
const StatementsPage = lazy(() => import("@/pages/firm/StatementsPage"));
const AuditLog = lazy(() => import("@/pages/firm/AuditLog"));
const FirmAMA = lazy(() => import("@/pages/firm/FirmAMA"));
const TenantSettings = lazy(() => import("@/pages/firm/TenantSettings"));
const SchedulesPage = lazy(() => import("@/pages/firm/SchedulesPage"));
const BulkImport = lazy(() => import("@/pages/firm/BulkImport"));
const DataMigration = lazy(() => import("@/pages/firm/DataMigration"));
const TenantUsers = lazy(() => import("@/pages/firm/TenantUsers"));
const FirmScorecard = lazy(() => import("@/pages/firm/FirmScorecard"));
const FirmTeam = lazy(() => import("@/pages/firm/FirmTeam"));
const ClientDashboard = lazy(() => import("@/pages/portal/ClientDashboard"));
const ClientStatements = lazy(() => import("@/pages/portal/ClientStatements"));
const ClientSchedules = lazy(() => import("@/pages/portal/ClientSchedules"));
const ClientAMA = lazy(() => import("@/pages/portal/ClientAMA"));
const ClientScorecard = lazy(() => import("@/pages/portal/ClientScorecard"));
const ClientGlossary = lazy(() => import("@/pages/portal/ClientGlossary"));

function RouteFallback() {
  return (
    <div className="flex h-screen items-center justify-center" data-testid="route-loading">
      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
    </div>
  );
}

function RootRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={(user.role === "client" || user.role === "manager") ? "/portal" : "/firm"} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <ImpersonationBanner />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/set-password" element={<SetPassword />} />

            <Route
              path="/firm"
              element={
                <ProtectedRoute roles={["firm_admin", "firm_staff"]}>
                  <FirmLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<FirmDashboard />} />
              <Route path="team" element={<FirmTeam />} />
              <Route path="t/:tid" element={<TenantLayout />}>
                <Route index element={<TenantOverview />} />
                <Route path="import" element={<ImportWizard />} />
                <Route path="mapping" element={<AccountMapping />} />
                <Route path="statements" element={<StatementsPage />} />
                <Route path="scorecard" element={<FirmScorecard />} />
                <Route path="schedules" element={<SchedulesPage />} />
                <Route path="bulk-import" element={<BulkImport />} />
                <Route path="migrate" element={<DataMigration />} />
                <Route path="ama" element={<FirmAMA />} />
                <Route path="users" element={<TenantUsers />} />
                <Route path="audit" element={<AuditLog />} />
                <Route path="settings" element={<TenantSettings />} />
              </Route>
            </Route>

            <Route
              path="/portal"
              element={
                <ProtectedRoute roles={["client", "manager"]}>
                  <PortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<ClientDashboard />} />
              <Route path="statements" element={<ClientStatements />} />
              <Route path="scorecard" element={<ClientScorecard />} />
              <Route path="glossary" element={<ClientGlossary />} />
              <Route path="schedules" element={<ClientSchedules />} />
              <Route path="ama" element={<ClientAMA />} />
            </Route>

            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
